import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  AfterViewInit,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { merge, noop, Observable, of } from 'rxjs';
import { debounceTime, filter, switchMap, tap, combineLatestWith } from 'rxjs/operators';
import {
  Project,
  ProjectPagination,
  Pipeline,
  KanbanDefinition,
  GridDefinition,
} from 'app/modules/projects/projects.types';
import { ProjectStage, FieldDef, Account, QueryConfig } from 'app/core/models';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { ProjectAddComponent } from 'app/modules/projects/modals/project-add/project-add.component';
import { ProjectsListComponent } from 'app/modules/projects/list/list.component';
import { AccountService, AuthService } from 'app/core';
import sortBy from 'lodash-es/sortBy';
import get from 'lodash-es/get';
import { ProjectEditPipelineComponent } from '../../modals/project-edit-pipeline/project-edit-pipeline.component';
import UrlUtils from 'app/shared/Utils/url.utils';
import { ProjectStatusRequiredFieldsComponent } from '../../modals/project-status-required-fields/project-status-required-fields.component';
import { LayoutService } from 'app/layout/layout.service';
import { PipelineService } from '../../pipeline.service';
import { FoldersTabService } from 'app/shared/modules/folders/folders.service';
import { Dictionary } from 'app/shared/models/dictionary';
import { RelatedToType } from 'app/shared/enums';
import { FieldLayoutOptions } from 'app/shared/components/field-layout-edit/field-layout-edit.types';
import { FieldLayoutEditComponent } from 'app/shared/components/field-layout-edit/field-layout-edit.component';
import { setupAddDealByDrawer } from '../../@shared/functions.utils';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { FeatureFlag, NewPermission, Permission } from 'app/core/enums';
import { MatLegacySelectChange } from '@angular/material/legacy-select';
import { TsSnackbarService } from 'tsui';
import { AccountState } from '@state/account/state';
import { Store } from '@ngxs/store';
import { UserState } from '@state/user/state';
import {
  getAllowedFields,
  getHeaderLabel,
  getLinkedFieldValue as _getLinkedFieldValue,
  isLinkedFieldColumn,
} from 'app/shared/functions';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { SelectFieldComponent } from '@shared/components/widgets/select-field/select-field.component';

export class Group {
  fieldName: string;
  fieldVal: any;
  label: string;
  totalCount = 0;
  summaryColumns: any = {};
  vals: Project[] = [];
}

@Component({
  selector: 'projects-list-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsListGridComponent implements OnInit, AfterViewInit {
  private account: Account;
  private projects: Project[];
  private displayColumns: string[] = [];
  private _pageSize: number = 20;
  private pipelines: Pipeline[];
  private fieldDefs: FieldDef[] = [];
  private groupBy: string = 'project_stage';
  private _defaultSort = [
    { field: 'project_stage_rank', order: 'asc' },
    { field: 'created_at', order: 'desc' },
  ];
  private readonly destroyRef = inject(DestroyRef);

  data: any;
  pipeline: Pipeline;
  pagination: ProjectPagination;
  isLoading: boolean;
  groupedProjects: Group[] = [];
  projectStages: ProjectStage[] = [];
  queryFilters: Partial<QueryConfig.QueryFilters>;
  savedFilter: any = null;
  uploadingFile: boolean = false;
  Permission = Permission;
  NewPermission = NewPermission;
  getHeaderLabel = getHeaderLabel;
  isLinkedFieldColumn = isLinkedFieldColumn;
  emptyFieldName = SelectFieldComponent.emptyFieldDef.name;

  defaultGridDefinition = [
    ['address_string', 'unit_size', 'updated_at'],
    ['broker_guidance_price', 'going_in_noi', 'rentable_square_feet'],
  ];

  @ViewChild(MatPaginator) private _paginator: MatPaginator;

  private get options(): Dictionary<any> {
    return this.data.options;
  }

  private get gridDefinition(): GridDefinition {
    return this.pipeline.grid_definition as GridDefinition;
  }

  private get projectTemplates(): { id: number; name: string }[] {
    return this.options.project_templates
      .filter(({ hidden, related_to_type }) => !hidden && related_to_type === RelatedToType.Project)
      .sort((a, b) => a.rank - b.rank);
  }

  get gridFields(): string[][] {
    return this.gridDefinition?.fields || this.defaultGridDefinition;
  }

  get canAddDeal(): boolean {
    return this.authService.hasPermission(Permission.DEALS_ADD);
  }

  get filteredDisplayColumns() {
    return this.displayColumns.filter((column) => column !== this.emptyFieldName);
  }

  constructor(
    public projectsListComponent: ProjectsListComponent,
    private _activatedRoute: ActivatedRoute,
    private _changeDetectorRef: ChangeDetectorRef,
    private _accountService: AccountService,
    private _projectsService: ProjectsService,
    private _router: Router,
    private _matDialog: MatDialog,
    private _snackBar: TsSnackbarService,
    private readonly layoutService: LayoutService,
    private readonly pipelineService: PipelineService,
    private readonly _foldersTabService: FoldersTabService,
    private readonly confirmService: ConfirmService,
    private readonly authService: AuthService,
    private readonly store: Store,
  ) {
    this.pagination = {
      length: 1,
      size: 1,
      page: 1,
      lastPage: 1,
      startIndex: 1,
      endIndex: 1 - 1,
    };

    this.displayColumns = [
      'id',
      'title',
      'project_stage_id',
      'city',
      'postal_code',
      'rentable_square_feet',
      'unit_size',
      'num_buildings',
      'street_address',
      'state',
      'updated_at',
      'created_at',
      'broker_guidance_price',
      'going_in_noi',
      'bid_price',
      'offer_price',
      'offer_sent_at',
      'noi',
      'occupancy',
      'property_type',
      'address_string',
    ];

    this.cleanseDisplayColumns();
  }
  private getNestedOptions(): any {
    return this.hasNestedFiltersOn ? { fieldDefs: this.fieldDefs } : null;
  }
  readonly hasNestedFiltersOn = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NESTED_FILTERS));

  ngOnInit(): void {
    this._activatedRoute.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: Data) => {
      this.data = data;
      this.pipeline = this.data.pipeline;
      UrlUtils.saveToLocalStorage('projectPipeline', this.pipeline.id);
      if (this.hasNestedFiltersOn) {
        this.fieldDefs = this.store.selectSnapshot(
          ObjectsManagerState.getFieldsByObjectName(RelatedToType.Project, {
            allowedFieldsOnly: true,
            addNestedFields: true,
          }),
        );
      } else {
        this.fieldDefs = getAllowedFields(data.fieldDefs, RelatedToType.Project);
      }
      this.projectStages = this.data.options.project_stages.filter((option: ProjectStage) => !option.hidden);
    });

    this._activatedRoute.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const currentPage = +sessionStorage.getItem('currentPage') || 0;
      if (params['filterId']) {
        // If filterId queryParam is equal to reset, reset query filters and update the deals.
        if (params['filterId'] == 'reset') {
          this.resetQueryFilters();
          this._projectsService
            .getProjects(
              this.filteredDisplayColumns,
              this.queryFilters,
              currentPage,
              this._pageSize,
              this._defaultSort,
              'asc',
              '',
              null,
              '',
              false,
              true,
              false,
              this.getNestedOptions(),
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(noop);
        }
        // Else filterId has an ID value, then fetch the related saved filter and update the deals.
        else {
          this._accountService
            .getSavedFilterById(parseInt(params['filterId']))
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              tap((savedFilter) => {
                this.savedFilter = savedFilter;
                this.queryFilters = JSON.parse(savedFilter.filters);
                this.saveQueryFilters(this.queryFilters);
                UrlUtils.saveToLocalStorage('savedFilter', this.savedFilter.id.toString(), false);
              }),
              switchMap(() =>
                this._projectsService.getProjects(
                  this.filteredDisplayColumns,
                  this.queryFilters,
                  currentPage,
                  this._pageSize,
                  this._defaultSort,
                  'asc',
                  '',
                  null,
                  '',
                  false,
                  true,
                  false,
                  this.getNestedOptions(),
                ),
              ),
            )
            .subscribe(noop);
        }
      } else {
        this.fillQueryFilters();
        let id = UrlUtils.getFromLocalStorage('savedFilter', false);
        if (id) {
          let url = this._router.url.split('?')[0];
          if (this.pipeline?.pipeline_type === 'static')
            this._router.navigate([url], { queryParams: { filterId: id } });
          else this._router.navigate([url]);
        }
        this.fillPageSize();
      }
    });

    this._projectsService.pipeline$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((pipeline: Pipeline) => {
      const currentPage = +sessionStorage.getItem('currentPage') || 0;
      this.pipeline = pipeline;
      let definition = this.pipeline.grid_definition;
      if (definition) {
        this.buildDisplayColumnsFromDefinition();
        this.cleanseDisplayColumns();
      } else {
        this.buildDisplayColumnsFromDefinition();
        this.cleanseDisplayColumns();
      }

      this.loadProjects();

      // [SC-32646] Replicated logic from table.component.ts to preserve saved filter on dynamic views
      if (this.isPipelineDynamic() && this.pipeline.saved_filter_id) {
        this._accountService
          .getSavedFilterById(this.pipeline.saved_filter_id)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            tap((savedFilter) => {
              this.savedFilter = savedFilter;
              this.queryFilters = JSON.parse(savedFilter.filters);
              this.saveQueryFilters(this.queryFilters);
              UrlUtils.saveToLocalStorage('savedFilter', this.savedFilter.id.toString(), false);
            }),
            switchMap(() =>
              this._projectsService.getProjects(
                this.filteredDisplayColumns,
                this.queryFilters,
                currentPage,
                this._pageSize,
                this._defaultSort,
                'asc',
                '',
                null,
                '',
                false,
                true,
                false,
                this.getNestedOptions(),
              ),
            ),
          )
          .subscribe(noop);
      } else {
        this._projectsService
          .getProjects(
            this.filteredDisplayColumns,
            this.queryFilters,
            currentPage,
            this._pageSize,
            this._defaultSort,
            'asc',
            '',
            null,
            '',
            false,
            true,
            false,
            this.getNestedOptions(),
          )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(noop);
      }

      this._changeDetectorRef.markForCheck();
    });

    this._projectsService.pipelines$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((pipelines: Pipeline[]) => {
      if (pipelines) {
        this.pipelines = pipelines;
      }
    });

    UrlUtils.saveToLocalStorage('projectView', 'grid');

    this._accountService.currentAccount.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((accountData) => {
      this.account = accountData;
    });
  }

  ngAfterViewInit(): void {
    if (this._paginator) {
      merge(this._paginator.page)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          switchMap(() => {
            this.isLoading = true;
            sessionStorage.setItem('currentPage', `${this._paginator.pageIndex}`);
            this.savePageSize();
            return this._projectsService.getProjects(
              this.filteredDisplayColumns,
              this.queryFilters,
              this._paginator.pageIndex,
              this._paginator.pageSize,
              this._defaultSort,
              'asc',
              '',
              null,
              '',
              false,
              true,
              false,
              this.getNestedOptions(),
            );
          }),
        )
        .subscribe(() => (this.isLoading = false));
    }
  }

  private loadProjects(): void {
    this._projectsService.projects$
      .pipe(
        combineLatestWith(this._projectsService.pagination$),
        takeUntilDestroyed(this.destroyRef),
        filter(([projects, pagination]) => !!projects && !!pagination),
        tap(([projects, pagination]) => {
          this.projects = projects;
          this.pagination = pagination;
          this.groupProjects();
          this._changeDetectorRef.markForCheck();
        }),
      )
      .subscribe(noop);
  }

  private groupProjects(): void {
    this.groupedProjects = [];
    const OTHER = 'Other';
    const groupedBy = `${this.groupBy}.name`;

    // Get visible options and sort them
    let visibleOptions: ProjectStage[] = this.data.options.project_stages?.filter((option) => {
      if (this.pipeline.pipeline_type === 'dynamic') {
        return true;
      }

      return option?.pipeline_ids?.includes(this.pipeline.id);
    });
    let sortedOptions = sortBy(visibleOptions, 'rank');

    // Create a map of all possible groups, including 'Other'
    let groupMap = new Map<string, Group>();
    sortedOptions.forEach((option) => {
      groupMap.set(option.name, new Group());
      groupMap.get(option.name).label = option.name;
      groupMap.get(option.name).fieldName = groupedBy;
    });
    groupMap.set(OTHER, new Group());
    groupMap.get(OTHER).label = OTHER;
    groupMap.get(OTHER).fieldName = groupedBy;

    // Group projects
    this.projects.forEach((project) => {
      const stageName = get(project, groupedBy, OTHER);
      const group = groupMap.get(stageName) || groupMap.get(OTHER);
      group.vals.push(project);
    });

    // Convert map to array
    this.groupedProjects = Array.from(groupMap.values());
    // Filter out empty groups
    this.groupedProjects = this.groupedProjects.filter((group) => group.vals.length > 0 || group.label === OTHER);
  }

  private buildDisplayColumnsFromDefinition(): void {
    this.displayColumns = [];
    for (let col of this.gridFields) {
      for (let field of col) {
        if (!this.displayColumns.includes(field)) {
          this.displayColumns.push(field);
        }
      }
    }
  }

  private cleanseDisplayColumns(): void {
    const index = this.displayColumns.findIndex((item) => item === 'title');
    if (index > -1) {
      this.displayColumns.splice(index, 1);
      this.displayColumns.unshift('title');
    }
  }

  isPipelineDynamic(): boolean {
    return this.pipeline?.pipeline_type === 'dynamic';
  }

  public hideSaveButton(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return !this.store.selectSnapshot(UserState.hasPermission(NewPermission.DEALS_PUBLIC_CUSTOM_VIEWS));
    }

    return !this.store.selectSnapshot(UserState.hasPermission(Permission.PIPELINE_EDIT_DYNAMIC_FILTER));
  }

  // Disable the dropdown option if its not associated to the pipeline
  disableDropdownOptions(option: ProjectStage, deal: Project): boolean {
    if (this.pipeline.pipeline_type === 'dynamic') {
      return !option?.pipeline_ids?.includes(+deal.pipeline_id);
    }

    return !option?.pipeline_ids?.includes(this.pipeline.id);
  }

  onUpdate(event: MatLegacySelectChange, deal: Project): void {
    const currentStage = this.projectStages.find((option) => option.id === Number(deal.project_stage_id));
    const stage = this.projectStages.find((option) => option.id === Number(event.value));
    deal.project_stage_id = stage.id;
    deal.project_stage = stage;

    let observable: Observable<any>;

    if (stage.required_fields?.length) {
      const project = this.projects.find((proj) => proj.id == deal.id);

      observable = this._matDialog
        .open(ProjectStatusRequiredFieldsComponent, {
          panelClass: 'mailbox-compose-dialog',
          data: { stage, project, data: this.data },
        })
        .afterClosed()
        .pipe(
          tap((result: { updatedProject: Project }) => {
            if (!result) {
              deal.project_stage_id = currentStage.id;
              deal.project_stage = currentStage;
            }
          }),
        );
    } else {
      observable = this._projectsService.saveProject(deal);
    }

    observable
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.groupDealsAndDetectChanges()),
      )
      .subscribe(noop);
  }

  createProjectByUrl(): void {
    const dialogRef = this._matDialog.open(ProjectAddComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: this.data,
    });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(noop);
  }

  onProjectFileChanged(event): void {
    let file = event.target.files[0];
    this.uploadingFile = true;
    this._projectsService
      .uploadOm(file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((document) => {
        this.uploadingFile = false;
        this._router.navigate(['/projects', this.pipeline.id, 'pdf'], {
          queryParams: { pdf_url: encodeURIComponent(document.pdf_url), document_id: document.id },
        });
      });
  }

  private saveQueryFilters(filters: any): void {
    UrlUtils.saveToLocalStorage('Project_queryFilters', filters, true);
  }

  private fillQueryFilters(): void {
    this.queryFilters = UrlUtils.getFromLocalStorage('Project_queryFilters', true);
  }

  private savePageSize(): void {
    UrlUtils.saveToLocalStorage('pageSize', this._paginator.pageSize.toString());
  }

  private fillPageSize(): void {
    let pageSize = UrlUtils.getFromLocalStorage('pageSize');
    if (pageSize) {
      this._pageSize = parseInt(pageSize);
    }
  }

  deleteProject(project: Project): void {
    const projectIsParent = project.is_parent === 'true';
    this.confirmService
      .openConfirmationModal({
        message: `Are you sure you would like to delete ${project.title}?${
          projectIsParent ? '\nThis will delete all related child deals as well.' : ''
        }`,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((result: boolean) => result),
      )
      .subscribe(() => {
        const id = project.id;
        this._projectsService
          .deleteProject(id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this._paginator._changePageSize(this._paginator.pageSize);
            this._changeDetectorRef.markForCheck();
          });
      });
  }

  moveDeal(project: Project): void {
    const dialogRef = this._matDialog.open(ProjectEditPipelineComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { project: project, pipelines: this.pipelines, data: this.data, currentPipeline: this.pipeline },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result && result.moved) {
          this._snackBar.open('Deal has been moved.', 'success');
          this._projectsService.updateProjectSubjectValue(this.projects.filter(({ id }) => result.project.id !== id));
          this._paginator._changePageSize(this._paginator.pageSize);
          this._changeDetectorRef.markForCheck();
        }
      });
  }

  navigateToProjectsAddMap(): void {
    this._router.navigate(['/projects', this.pipeline.id, 'map', 'add']);
  }

  searchInput(event: string): void {
    this.isLoading = true;
    this._projectsService
      .getProjects(
        this.filteredDisplayColumns,
        this.queryFilters,
        0,
        this._pageSize,
        'project_stage_rank',
        'asc',
        event,
        null,
        '',
        false,
        true,
        false,
        this.getNestedOptions(),
      )
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isLoading = false;
      });
  }

  filterProjects(event): void {
    this.isLoading = true;
    this._projectsService
      .getProjects(
        this.filteredDisplayColumns,
        event,
        0,
        this._pageSize,
        'project_stage_rank',
        'asc',
        '',
        null,
        '',
        false,
        true,
        false,
        this.getNestedOptions(),
      )
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isLoading = false;
      });
  }

  private resetQueryFilters(): void {
    this.queryFilters = {};
    this.saveQueryFilters(this.queryFilters);
    this.savedFilter = null;
    UrlUtils.saveToLocalStorage('savedFilter', '', false);
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  openCustomDrawer(): void {
    setupAddDealByDrawer(
      {
        projectService: this._projectsService,
        pipelineService: this.pipelineService,
        foldersTabService: this._foldersTabService,
        layoutService: this.layoutService,
        router: this._router,
        matDialog: this._matDialog,
        authService: this.authService,
      },
      {
        pipeline: this.pipeline,
        account: this.account,
        fieldDefs: this.fieldDefs,
        data: this.data,
        projectTemplates: this.projectTemplates,
        createPortfolio: true,
      },
    ).subscribe(noop);
  }

  editLayout(): void {
    of(this.pipeline)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(({ grid_definition }) => {
          return grid_definition
            ? of(this.pipeline)
            : this.updateGridDefinition({ fields: this.defaultGridDefinition });
        }),
        tap((pipeline: Pipeline) => {
          this.pipeline = pipeline;

          this.layoutService.openComponentDrawer<FieldLayoutOptions>(FieldLayoutEditComponent, {
            inputs: {
              ui: { title: 'Edit grid layout' },
              data: { fieldDefs: this.fieldDefs },
              fields: this.getDataForEdit(),
              filterFieldsBy: RelatedToType.Project,
              hasNestedFiltersOn: this.hasNestedFiltersOn,
            },
            outputs: {
              onCancel: () => this.layoutService.closeComponentDrawer(),
              onSave: (fields: string[][]) => {
                this.isLoading = true;
                this.updateGridDefinition({ fields: this.transformFields(fields) })
                  .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    tap((updatedPipeline: Pipeline) => {
                      this.pipeline = updatedPipeline;
                      this.layoutService.closeComponentDrawer();
                    }),
                    switchMap(() =>
                      this._projectsService.getProjects(
                        this.filteredDisplayColumns,
                        this.queryFilters,
                        +sessionStorage.getItem('currentPage') || 0,
                        this._pageSize,
                        this._defaultSort,
                        'asc',
                        '',
                        null,
                        '',
                        false,
                        true,
                        false,
                        this.getNestedOptions(),
                      ),
                    ),
                    tap(() => {
                      this.loadProjects();
                      this.isLoading = false;
                    }),
                  )
                  .subscribe(noop);
              },
            },
          });
        }),
      )
      .subscribe(noop);
  }

  lookupField(name: string): FieldDef {
    if (name === SelectFieldComponent.emptyFieldDef.name) return SelectFieldComponent.emptyFieldDef;

    return this.fieldDefs.find((fieldDef) => fieldDef.name == name);
  }

  canDeleteDeal(deal: Project): any {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return [NewPermission.DEALS_DELETE, deal?.permissions?.is_deletable];
    }

    return Permission.DEALS_DELETE;
  }

  private updateGridDefinition(model: KanbanDefinition): Observable<Pipeline> {
    return this.pipelineService.update(
      { ...this.pipeline, grid_definition: JSON.stringify(model) },
      Number(this.account.id),
    );
  }

  private getDataForEdit(): string[][] {
    const maxColumnCount = this.getMaxColumnCount(this.gridFields);
    const columnsAndRows = [];

    for (let i = 0; i < maxColumnCount; i++) {
      const columnArray = [];
      for (let j = 0; j < this.gridFields.length; j++) {
        if (j !== this.gridFields.length - 1 || this.gridFields[j][i]) {
          columnArray.push(this.gridFields[j][i]);
        }
      }
      columnsAndRows.push(columnArray);
    }

    return columnsAndRows;
  }

  private getMaxColumnCount(fields: string[][]): number {
    return fields.reduce((prev: number, cur: string[]) => {
      return cur.length > prev ? cur.length : prev;
    }, 0);
  }

  private transformFields(fields: string[][]): string[][] {
    const maxColumnCount = this.getMaxColumnCount(fields);
    const columnsAndRows = [];

    for (let i = 0; i < maxColumnCount; i++) {
      const columnArray = [];
      for (let j = 0; j < fields.length; j++) {
        if (fields[j][i]) {
          columnArray.push(fields[j][i]);
        }
      }
      columnsAndRows.push(columnArray);
    }

    return columnsAndRows;
  }

  private groupDealsAndDetectChanges(): void {
    this.groupProjects();
    this._changeDetectorRef.detectChanges();
  }

  getLinkedFieldValue(object: any, fieldName: string): { label: string; [key: string]: any }[] {
    return _getLinkedFieldValue(object, fieldName, {
      fieldDefs: this.fieldDefs,
      options: this.options,
    });
  }
}
